import { Component, OnChanges, OnInit, OnDestroy, Input, Output, SimpleChanges, EventEmitter, ChangeDetectionStrategy, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, Validators, AbstractControl, ValidatorFn } from "@angular/forms";
import { CustomValidator } from "app/shared/utilities/custom-validators.models";
import { UtilityFunctions } from "app/shared/utilities/functions.models";
import moment from "moment";
import { Subject } from "rxjs";
import { distinctUntilChanged, map, filter } from "rxjs/operators";


@Component({
    selector: 'app-custom-date-time-picker',
    templateUrl: './custom-date-time-picker.component.html',
    styleUrls: ['./custom-date-time-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomDateTimePickerComponent implements OnChanges, OnInit {

  @Input() enabledReactiveForm: boolean = false;
  @Input() dateTimeFormControl: AbstractControl = new FormControl();

  @Input() dateTime: string | Date; //dateTime used when enabledReactiveForm is false
  @Input() disabled: boolean = false; //disabled used when enabledReactiveForm is false
  @Input() isReadonly: boolean = true;
  @Input() isRequired: boolean = true; //IsRequired used when enabledReactiveForm is false
  @Input() min: string | Date;
  @Input() max: string | Date;
  @Input() disableClearBtn: boolean = false;
  @Input() disableInternalErrorMessage: boolean = false;
  @Input() enableCheckDateOnRange: boolean = true;
  @Input() error: boolean;
  @Input() customBtn: boolean = false;
  @Input() btnText: string = 'body';
  @Input() dateNotSelectableCustomText: string = '';

  @Output() dateTimeEmit = new EventEmitter<string>();
  @Output() customBtnClick = new EventEmitter<void>();

  dateForm = new FormGroup({
    date: new FormControl(null),
    min: new FormControl(null),
    max: new FormControl(null),
  });

  isDateNotSelectable = false;
  isMobile = false;

  private destroyRef = inject(DestroyRef);

  constructor() {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.enabledReactiveForm && this.enabledReactiveForm) {
      if (changes.dateTimeFormControl) {
        this.dateForm.get('date').setValidators(this.dateTimeFormControl.validator);
        this.initFormControl();
      }
    } else {
      if (changes.dateTime) {
        this.dateForm.get('date').setValue(this.dateTime);
      }

      if (changes.isRequired) {
        this.dateForm.get('date').setValidators(this.isRequired ? Validators.required : null);
      }

      if (changes.disabled) {
        this.disabled ? this.dateForm.get('date').disable() : this.dateForm.get('date').enable();
      }
    }

    if (changes.min) {
      this.dateForm.get('min').setValue(this.min);
      this.dateForm.updateValueAndValidity();
    }

    if (changes.max) {
      this.dateForm.get('max').setValue(this.max);
      this.dateForm.updateValueAndValidity();
    }

    if (changes.enableCheckDateOnRange) {
      this.enableCheckDateOnRange
        ? this.dateForm.setValidators(CustomValidator.dateRangeValidator('date', 'min', 'max'))
        : this.dateForm.setValidators([]);
      this.dateForm.updateValueAndValidity();
    }

    this.isDateNotSelectable = this.min && this.max && moment(this.min).isAfter(moment(this.max));
    if (this.isDateNotSelectable) this.dateForm.disable();
  }

  ngOnInit(): void {
    this.isMobile = UtilityFunctions.detectCurrentOS().isMobile;
  }


  onResetDate() {
    this.dateForm.get('date').markAsTouched();
    this.dateForm.get('date').markAsDirty();
    this.dateForm.get('date').setValue(null);
  }

  onCustomBtnClick() {
    if (this.dateForm.get('date').value) this.customBtnClick.emit();
  }

  private init() {
    this.dateForm.statusChanges.subscribe(() => {
      setTimeout(() => {
        if (this.dateForm.invalid && this.dateForm?.errors?.dateRange) {
          console.error(this.dateForm.errors?.dateRange);
          this.onResetDate();
        }
      },);
    });

    this.dateForm.get('date').valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
      map(value => {
        if (value) {
          const valueDate = moment(value).toDate();
          const date = moment(valueDate).format('YYYY-MM-DD');
          return UtilityFunctions.fromDateStringTODateISOString(date);
        } else {
          return null;
        }
      }),
    ).subscribe((value) => {
      if (this.dateForm.get('date').dirty && this.enabledReactiveForm) {
        this.dateTimeFormControl.setValue(value);
      } if (this.dateForm.get('date').dirty && !this.enabledReactiveForm) {
        this.dateForm.get('date').markAsPristine();
        this.dateTimeEmit.emit(value);
      }
    });
  }

  private initFormControl() {
    this.dateForm.get('date').setValue(this.dateTimeFormControl.value);
    this.dateForm.updateValueAndValidity();
    this.dateTimeFormControl.valueChanges.pipe(
      distinctUntilChanged(),
      filter(value => value !== this.dateForm.get('date').value),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((value) => {
      this.dateForm.get('date').markAsPristine();
      this.dateForm.get('date').setValue(value, { emitEvent: false });
    });

    this.dateTimeFormControl.statusChanges.subscribe((status) => {
      if (status === 'DISABLED') {
        this.dateForm.get('date').disable();
      } else {
        this.dateForm.get('date').enable();
      }
    });
  }

  private getValidators(control: AbstractControl): ValidatorFn[] {
    return control.validator ? [control.validator] : [];
  }

  private removeValidator(controlName: string, validatorToRemove: ValidatorFn): void {
    const control = this.dateForm.get(controlName);
    if (!control) {
      return;
    }

    const currentValidators = this.getValidators(control);
    const updatedValidators = currentValidators.filter(validator => validator !== validatorToRemove);

    control.setValidators(updatedValidators);
    control.updateValueAndValidity();
  }

  get dateControl(): FormControl {
    return this.dateForm.get('date') as FormControl;
  }
}
